import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useParams, useHistory } from 'react-router-dom';
import { AnalyticsEventSource, ScreenEventAnalytics } from '@atlassian/temp-analytics';
import { CCPPricing } from '@atlassian/cssxp-typescommon';
import { Text, Flex } from '@atlaskit/primitives';
import EditIcon from '@atlaskit/icon/core/edit';
import Button from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import {
  useFeatureToggle,
  AppContent,
  AppPage,
  useNotification,
  errorNotificationBuilder,
  NotificationTypes,
} from '@css-xp/base';
import { PageBackgroundV2 } from '@css-xp/page-background';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { STATSIG_FEATURE_GATES, routePaths } from '@css-xp/ppc-constants';
import { SelectBillingProfilePageLoader } from '@css-xp/ppc-page-loaders';
import {
  CommerceAddress,
  FetchCustomersForTransactionAccountQuery,
  FetchCustomersForTransactionAccountQueryVariables,
  FetchTransactionAccountForCreateBillingProfileQuery,
  FetchTransactionAccountForCreateBillingProfileQueryVariables,
  InvoiceGroup,
} from '@css-xp/graphtypes/src/generated-types-new';
import { getCompanyNameFromRoute, getRouteUrl, useQueryParams } from '@css-xp/ppc-utils';
import { useStore } from './store';
import { useOpenClose } from '@css-xp/ppc-custom-hooks';
import { fetchTransactionAccountForCreateBillingProfile } from '@css-xp/ppc-billing-profile-table';
import { DestinationBillingProfileWrapper } from './ui/billing-profile/src/components/destination-billing-profile-wrapper';
import { fetchCustomers } from './queries/fetch-customers';
import { SearchCustomerContainer, SearchCustomerModal } from './ui/customer';
import { CartCustomerSearchItem } from './ui/customer/src/types';
import { CreateBillingProfileModalForOrderFlow } from './ui/customer/src/components/create-billing-profile-modal';
import { transformBillingProfileDetails } from './utils';
import { BillingProfileResponse } from './types';
import { CART_ROUTE_PARAMS } from './constants';

export const SelectBillingProfilePage = () => {
  const { txaId } = useParams<{ txaId: string }>();
  const [isEnabled] = useFeatureToggle();
  const queryParams = useQueryParams();
  const encodedCustomerName = queryParams.get(CART_ROUTE_PARAMS.CUSTOMER) as string;
  const isSupportForPaypalEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_PAYPAL_SUPPORT);
  const isCustomerContactV3Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_CUSTOMER_CONTACT_V3);
  const isFetchBillingProfilesViaCCPEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_BILLING_PROFILE_FROM_CCP);
  const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);
  const isNonCommercialPricingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING);
  const isDesignIteration1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1);
  const isSoldToAddressArchivalEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ARCHIVE_SOLD_TO_ADDRESS);
  const isAchPaymentMethodFFEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ACH_DIRECT_DEBIT);
  const [customersDetails, setCustomersDetails] = useState<CommerceAddress[]>([]);
  const { sendErrorNotification } = useNotification();
  const { isOpen, open: openCreateBillingProfileModal, close: closeCreateBillingProfileModal } = useOpenClose();
  const [showSearchCustomerContainer, setShowSearchContainer] = useState(false);
  const initialTxaIdRef = useRef(txaId);
  const history = useHistory();
  const [{ selectedCustomer }, actions] = useStore();
  const customerIds = selectedCustomer?.ids;

  const humanReadableCustomerName = getCompanyNameFromRoute(encodedCustomerName);

  const { data: transactionAccountData, loading: transactionAccountDataLoading } = useQuery<
    FetchTransactionAccountForCreateBillingProfileQuery,
    FetchTransactionAccountForCreateBillingProfileQueryVariables
  >(fetchTransactionAccountForCreateBillingProfile, { variables: { id: txaId } });

  const { data, loading, error } = useQuery<
    FetchCustomersForTransactionAccountQuery,
    FetchCustomersForTransactionAccountQueryVariables
  >(fetchCustomers, {
    variables: {
      id: txaId,
      viewTaxRelatedInfo: false,
    },
    fetchPolicy: 'cache-and-network',
    skip: !txaId || (!!customersDetails && customersDetails?.length > 0),
  });

  useEffect(() => {
    if (humanReadableCustomerName && customerIds) {
      actions.setSelectedCustomer({ name: humanReadableCustomerName, ids: customerIds });
    }
  }, [humanReadableCustomerName, customerIds, actions]);

  const getSelectedCustomerDetails = (customerDetails: CommerceAddress[]) => {
    const shipToDetails = customerDetails?.filter(
      (node: any) => node?.name?.toLowerCase() === humanReadableCustomerName?.toLowerCase()
    );
    if (shipToDetails?.length) {
      const selectedCustomerName = shipToDetails?.[0]?.name;
      const selectedCustomerIds = shipToDetails?.reduce((acc: string[], node: any) => {
        if (isSoldToAddressArchivalEnabled ? node?.isActive : true) {
          node?.id && acc.push(node.id);
        }
        return acc;
      }, []);
      if (selectedCustomerName && selectedCustomerIds?.length > 0) {
        actions.setSelectedCustomer({ name: selectedCustomerName, ids: selectedCustomerIds });
      }
      customerDetails && setCustomersDetails(customerDetails);
    }
  };

  const notifyError = () => {
    const notification = errorNotificationBuilder()
      .withHeader(`Not able to fetch the customer list`)
      .withMessage(`Please reload the page. If the issue persists, please contact us.`)
      .isVersion(NotificationTypes.FLAG)
      .build();
    sendErrorNotification(notification);
  };

  if (!loading && data) {
    getSelectedCustomerDetails(data?.transactionAccount?.availableShipToParties?.nodes as CommerceAddress[]);
  } else if (error) {
    notifyError();
  }

  /**
   * If the `txaId` is changed from the orders screen,
   * redirecting the user to the select customer screen to restart the flow.
   */

  useEffect(() => {
    if (initialTxaIdRef.current !== txaId) {
      actions.resetStore();
      history.push(getRouteUrl(routePaths.cart, { txaId }));
    }
  }, [txaId]);

  const handleCustomerChange = (customer: CartCustomerSearchItem) => {
    actions.setSelectedCustomer(customer);
    actions.resetStore();
    setShowSearchContainer(false);
    const companyName = encodeURIComponent(customer.name);
    const params = new URLSearchParams({ customer: companyName });
    history.push({
      pathname: getRouteUrl(routePaths.cart, {
        txaId: txaId as string,
      }),
      search: params.toString(),
    });
  };

  const storeBillingProfiles = (billingProfiles: InvoiceGroup[]) => {
    const billingProfilesForStore = billingProfiles
      ?.map(({ id, name, shipToPriceEligibility, currency }) => {
        return {
          id: id ?? '',
          name: name ?? '',
          shipToPriceEligibility: shipToPriceEligibility as CCPPricing,
          currency: currency as `${generatedTypesNew.Currency}`,
        };
      })
      ?.sort((a, b) => (a.name < b.name ? -1 : 1));

    actions.setBillingProfiles(billingProfilesForStore);
  };

  const redirectToOrdersPage = (invoiceGroupId: string, hasCreatedNewBillingProfile?: boolean) => {
    const params = new URLSearchParams({
      billingProfile: invoiceGroupId,
      customer: encodedCustomerName,
    });
    history.push(
      {
        pathname: getRouteUrl(routePaths.cart, {
          txaId: txaId as string,
        }),
        search: params.toString(),
      },
      {
        hasCreatedNewBillingProfile: hasCreatedNewBillingProfile,
      }
    );
  };

  const onBillingProfileSubmit = (responsePayload?: {
    customerName?: string | null;
    customerId?: string;
    billingProfileInfo?: BillingProfileResponse;
  }) => {
    if (responsePayload?.billingProfileInfo?.id && responsePayload?.billingProfileInfo?.name) {
      actions.setInvoiceGroup(responsePayload?.billingProfileInfo?.id);
      actions.setBillingProfiles([transformBillingProfileDetails(responsePayload?.billingProfileInfo)]);
    }
    closeCreateBillingProfileModal();
    redirectToOrdersPage(responsePayload?.billingProfileInfo?.id as string, true);
  };

  const renderCreateBillingProfileModal = () => {
    return (
      <CreateBillingProfileModalForOrderFlow
        onSuccess={onBillingProfileSubmit}
        isOpen={isOpen}
        transactionAccountId={txaId}
        onClose={closeCreateBillingProfileModal}
        customer={{ name: selectedCustomer?.name as string, ids: selectedCustomer?.ids as string[] }}
        transactionAccountData={transactionAccountData}
        transactionAccountLoading={transactionAccountDataLoading}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
        isSoldToAddressArchivalEnabled={isSoldToAddressArchivalEnabled}
      />
    );
  };

  const isCustomerSelected = selectedCustomer && selectedCustomer?.ids?.length > 0;

  const getCustomerDetailsSection = () => {
    return showSearchCustomerContainer ? (
      <SearchCustomerContainer
        onSelectCustomer={handleCustomerChange}
        selectedCustomer={selectedCustomer}
        customerDetails={customersDetails}
        isSoldToAddressArchivalEnabled={isSoldToAddressArchivalEnabled}
      />
    ) : (
      <Flex alignItems="center" gap="space.100">
        <Text weight="bold">{selectedCustomer?.name}</Text>
        <Button
          iconBefore={EditIcon}
          onClick={() => setShowSearchContainer(true)}
          appearance="subtle"
          color={token('color.text.subtle')}
        >
          Change
        </Button>
      </Flex>
    );
  };
  const renderAppPage = () => {
    return (
      <AppPage className="app-page-v2 invoice-details-page">
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.UOF_SELECT_BILLING_PROFILE_SCREEN,
            attributes: {},
          }}
        />
        <AppContent>
          {loading ? (
            <SelectBillingProfilePageLoader />
          ) : (
            isCustomerSelected && (
              <>
                {getCustomerDetailsSection()}
                <DestinationBillingProfileWrapper
                  transactionAccountId={txaId}
                  customer={selectedCustomer as CartCustomerSearchItem}
                  isPaypalEnabled={isSupportForPaypalEnabled}
                  shouldShowACHPaymentMethod={isAchPaymentMethodFFEnabled}
                  showPrimaryContacts={isCustomerContactV3Enabled}
                  isFetchBillingProfilesViaCCPEnabled={isFetchBillingProfilesViaCCPEnabled}
                  isNewBPCreationFlowEnabled={isMultiCurrencyChangesEnabled || isNonCommercialPricingEnabled}
                  version={2}
                  onSubmit={(billingProfileId) => {
                    actions.setInvoiceGroup(billingProfileId);
                    redirectToOrdersPage(billingProfileId);
                  }}
                  onBillingProfilesFetch={(billingProfiles) => {
                    storeBillingProfiles(billingProfiles);
                  }}
                  openCreateBillingProfile={() => {
                    openCreateBillingProfileModal();
                  }}
                />
              </>
            )
          )}
          {isOpen && renderCreateBillingProfileModal()}
        </AppContent>
      </AppPage>
    );
  };

  const breadcrumbsItems = [
    {
      title: 'Home',
      href: routePaths.home,
    },
    {
      title: 'Select customer',
      href: routePaths.cart,
    },
    {
      title: humanReadableCustomerName as string,
      href:
        getRouteUrl(routePaths.cart, {
          txaId: txaId as string,
        }) + `?customer=${encodedCustomerName}`,
    },
  ];

  return (
    <PageBackgroundV2 heading={'Select billing profile'} breadcrumbsProps={{ items: breadcrumbsItems }}>
      {renderAppPage()}
    </PageBackgroundV2>
  );
};
